import MarkdownExit from '@w/md'
import { type Block, type Blocks, type BlockTable, type BlockTD, type ExtractBlock, unwrapReviewTableCells, unwrapReviewTableRows } from './block'
import { cellInline } from './cell-inline'

export function tableGrid(table: BlockTable): BlockTD[][] {
  const rows = unwrapReviewTableRows(table.children)
  const grid: BlockTD[][] = rows.map(() => [])
  rows.forEach((row, y) => {
    let x = 0
    for (const cell of unwrapReviewTableCells(row.children)) {
      while (grid[y][x]) x++
      const height = cell.rowSpan ?? 1
      const width = cell.colSpan ?? 1
      if (!Number.isSafeInteger(height) || height < 1 ||
        !Number.isSafeInteger(width) || width < 1 || y + height > rows.length) {
        throw new Error('Invalid table cell span')
      }
      for (let dy = 0; dy < height; dy++) {
        for (let dx = 0; dx < width; dx++) {
          if (grid[y + dy][x + dx]) throw new Error('Overlapping table cell spans')
          grid[y + dy][x + dx] = cell
        }
      }
      x += width
    }
  })
  return grid
}

interface HtmlElement {
  tag: string
  attrs: Record<string, string>
  children: (HtmlElement | string)[]
}

const htmlUtils = MarkdownExit().utils
const decode = (text: string) => htmlUtils.unescapeAll(text.replaceAll('\\', '\\\\'))
const escape = (text: string) => htmlUtils.escapeHtml(text)

function htmlTree(html: string): HtmlElement {
  const root: HtmlElement = { tag: 'root', attrs: {}, children: [] }
  const stack = [root]
  for (const match of html.matchAll(/<!--[\s\S]*?-->|<\/?[a-z][^>"']*(?:(?:"[^"]*"|'[^']*')[^>"']*)*>|[^<]+|</gi)) {
    const token = match[0]
    const parent = stack.at(-1)
    if (!parent) throw new Error('Unexpected HTML closing tag')
    if (token.startsWith('<!--')) continue
    if (!token.startsWith('<') || token === '<') {
      parent.children.push(decode(token))
      continue
    }
    const tagMatch = /^<\/?([\w-]+)/.exec(token)
    if (!tagMatch) throw new Error('Invalid HTML tag')
    const tag = tagMatch[1].toLowerCase()
    if (token.startsWith('</')) {
      if (stack.at(-1)?.tag !== tag) throw new Error(`Unbalanced HTML table tag: ${tag}`)
      stack.pop()
      continue
    }
    const attrs: Record<string, string> = {}
    for (const attr of token.slice(tag.length + 1).matchAll(/([\w-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g)) {
      attrs[attr[1].toLowerCase()] = decode(attr[2] ?? attr[3] ?? attr[4])
    }
    const node: HtmlElement = { tag, attrs, children: [] }
    parent.children.push(node)
    if (!['br', 'hr', 'img', 'col', 'input', 'wbr'].includes(tag) && !token.endsWith('/>')) stack.push(node)
  }
  if (stack.length !== 1) throw new Error('Unclosed HTML table')
  return root
}

function markdown(node: HtmlElement | string): string {
  // HTML text is literal: `$` would open a formula.
  if (typeof node === 'string') return node.replaceAll(/([\\`*_[\]{}<>#!|~+=.$-])/g, '\\$1').replaceAll('&', '&amp;')
  const content = node.children.map(markdown).join('')
  const formatted = content.replaceAll(/^ +| +$/g, (spaces) => '&#32;'.repeat(spaces.length))
  switch (node.tag) {
    case 'span': return node.attrs.lang ? `::l[${content}]{lang="${escape(node.attrs.lang)}"}` : content
    case 'strong': case 'b': return `**${formatted}**`
    case 'em': case 'i': return `*${formatted}*`
    case 's': case 'del': case 'strike': return `~~${formatted}~~`
    case 'u': return `::u[${content}]`
    case 'a': return `[${content}](${(node.attrs.href ?? '').replaceAll(/[()\\]/g, '\\$&')})`
    // A backslash break: a line of trailing spaces alone is a blank line, which drops a second break.
    case 'br': return '\\\n'
    case 'p': case 'div': return `${content}\n\n`
    // On its own line: a nested list follows its item's text.
    case 'ul': case 'ol': return '\n' + node.children.filter((child) => typeof child !== 'string').map((child, index) => {
      const item = markdown(child).trimEnd()
      return `${node.tag === 'ol' ? `${index + 1}.` : '-'} ${item.replaceAll('\n', '\n  ')}`
    }).join('\n') + '\n\n'
    case 'code': {
      const text = htmlText(node)
      const fence = '`'.repeat(Math.max(0, ...[...text.matchAll(/`+/g)].map((match) => match[0].length)) + 1)
      return fence + (text.startsWith('`') || text.endsWith('`') || /^ .* $/.test(text) ? ` ${text} ` : text) + fence
    }
    case 'pre': {
      const text = htmlText(node)
      const fence = '`'.repeat(Math.max(2, ...[...text.matchAll(/`+/g)].map((match) => match[0].length)) + 1)
      const code = node.children.find((child): child is HtmlElement => typeof child !== 'string' && child.tag === 'code')
      const language = code?.attrs.class?.match(/(?:^|\s)language-(\S+)/)?.[1] ?? ''
      return `${fence}${language}\n${text}\n${fence}\n\n`
    }
    case 'h1': case 'h2': case 'h3': return `${'#'.repeat(Number(node.tag[1]))} ${content}\n\n`
    // Blank lines before it too: after a text line `---` is a setext heading underline.
    case 'hr': return '\n\n---\n\n'
    case 'md-inline': return htmlText(node)
    case 'img': return `![${node.attrs.alt ?? ''}](${node.attrs.src ?? ''})`
    case 'mark': {
      const color = node.attrs.style?.match(/background-color:\s*([^;]+)/)?.[1] ?? 'default'
      return color === 'default' ? `==${content}==` : `::mark[${content}]{color="${color}"}`
    }
    case 'table': return htmlMarkup(node) + '\n\n'
    case 'blockquote': return content.trimEnd().split('\n').map((line) => `> ${line}`).join('\n') + '\n\n'
    default: return content
  }
}

export function parseHtmlTable(
  html: string,
  parse: (md: string, inline: boolean) => Blocks,
  nextId: () => string,
): BlockTable {
  const root = htmlTree(html)
  // A break takes the spaces before it, as a trailing-space break did (the reader writes a backslash break).
  const trimBeforeBreaks = (node: HtmlElement) => {
    node.children.forEach((child, index) => {
      const next = node.children[index + 1]
      if (typeof child !== 'string') trimBeforeBreaks(child)
      else if (typeof next !== 'string' && next?.tag === 'br') node.children[index] = child.replace(/ +$/, '')
    })
  }
  trimBeforeBreaks(root)
  const table = root.children.find((node): node is HtmlElement => typeof node !== 'string' && node.tag === 'table')
  if (!table) throw new Error('Expected HTML table')
  const rowNodes = table.children.flatMap((node) => typeof node === 'string' ? [] :
    ['thead', 'tbody', 'tfoot'].includes(node.tag) ? node.children : [node])
  const children = rowNodes.filter((node): node is HtmlElement => typeof node !== 'string' && node.tag === 'tr').map((row) => {
    const cells = row.children.filter((node): node is HtmlElement => typeof node !== 'string' && ['td', 'th'].includes(node.tag)).map((cell): BlockTD => {
      const spans: { rowSpan?: number; colSpan?: number } = {}
      for (const [attr, key] of [['rowspan', 'rowSpan'], ['colspan', 'colSpan']] as const) {
        if (cell.attrs[attr] === undefined) continue
        const span = Number(cell.attrs[attr])
        if (!Number.isSafeInteger(span) || span < 1) throw new Error('Invalid table cell span')
        if (span > 1) spans[key] = span
      }
      const block = (node: HtmlElement | string) => typeof node !== 'string' &&
        ['p', 'div', 'ul', 'ol', 'blockquote', 'pre', 'table', 'h1', 'h2', 'h3'].includes(node.tag)
      const divider = (node: HtmlElement | string) => typeof node !== 'string' && node.tag === 'hr'
      // A cell is an inline-level slot: block content (and a divider between text lines) reads as its inline content.
      const content: Blocks = []
      if (cell.children.some(block)) content.push(...parse(cell.children.map((node) => markdown(node)).join(''), false))
      else {
        let line: (HtmlElement | string)[] = []
        const flush = () => {
          if (line.length > 0) content.push(...parse(line.map((node) => markdown(node)).join(''), true))
          line = []
        }
        for (const node of cell.children) {
          if (divider(node)) {
            flush()
            content.push({ id: nextId(), type: 'hr' })
          } else line.push(node)
        }
        flush()
      }
      return Object.assign({ id: nextId(), type: 'td' as const, children: cellInline(content, nextId) }, spans,
        cell.tag === 'th' ? { _isHeader: true } : {})
    })
    return { id: nextId(), type: 'tr' as const, children: cells }
  })
  const result: BlockTable = { id: nextId(), type: 'table', children }
  const columns = table.children.flatMap((node) => typeof node !== 'string' && node.tag === 'colgroup'
    ? node.children.filter((child): child is HtmlElement => typeof child !== 'string' && child.tag === 'col') : [])
  const aligns = columns.filter((col) => 'align' in col.attrs).map((col) => col.attrs.align)
  const widths = columns.filter((col) => 'width' in col.attrs).map((col) => Number(col.attrs.width))
  if (aligns.length > 0) result._aligns = aligns
  if (widths.length > 0 && widths.every((width) => Number.isFinite(width) && width >= 0)) result._colWidths = widths
  // Imported row spans stop at the final row (the HTML import contract).
  children.forEach((row, y) => {
    row.children.forEach((cell) => {
    if (cell.rowSpan !== undefined && cell.rowSpan > children.length - y) {
      cell.rowSpan = children.length - y
      if (cell.rowSpan === 1) delete cell.rowSpan
    }
    })
  })
  tableGrid(result)
  return result
}

export function tableHasSpans(table: BlockTable): boolean {
  return unwrapReviewTableRows(table.children).some((row) => unwrapReviewTableCells(row.children).some((cell) =>
    (cell.rowSpan ?? 1) !== 1 || (cell.colSpan ?? 1) !== 1))
}

export function tableToHtml(table: BlockTable, contentHtml: (children: Blocks) => string): string {
  tableGrid(table)
  const columnCount = Math.max(table._aligns?.length ?? 0, table._colWidths?.length ?? 0)
  const columns = columnCount > 0 ? '<colgroup>' + Array.from({ length: columnCount }, (_, index) => {
    const align = table._aligns?.[index]
    const width = table._colWidths?.[index]
    return '<col' + (align === undefined ? '' : ` align="${escape(align)}"`) +
      (width === undefined ? '' : ` width="${width}"`) + '>'
  }).join('') + '</colgroup>' : ''
  return '<table>' + columns + unwrapReviewTableRows(table.children).map((row) => '<tr>' + unwrapReviewTableCells(row.children).map((cell) => {
    for (const span of [cell.rowSpan, cell.colSpan]) {
      if (span !== undefined && (!Number.isSafeInteger(span) || span < 1)) throw new Error('Invalid table cell span')
    }
    const attrs = (cell.rowSpan && cell.rowSpan > 1 ? ` rowspan="${escape(String(cell.rowSpan))}"` : '') +
      (cell.colSpan && cell.colSpan > 1 ? ` colspan="${escape(String(cell.colSpan))}"` : '')
    const tag = '_isHeader' in cell && cell._isHeader ? 'th' : 'td'
    return `<${tag}${attrs}>${contentHtml(cell.children)}</${tag}>`
  }).join('') + '</tr>').join('') + '</table>'
}

/** Blocks the document codec writes inside a paragraph; every other kind is block level. */
export const INLINE_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'br', 'text', 'chars', 'l', 'em', 'code', 'a', 'page', 'pageLink', 'pages', 'comment', 'criticComment', 'diff',
  'review', 'mention', 'cloze', 'question', 'img', 'preview', 'video', 'audio', 'file',
])

/** Inline cell content's own Markdown, written by the document codec. */
export type TableCellMarkdown = (blocks: Blocks) => string

type HtmlElementWriter<T extends Block['type']> = {
  /** Fields the element holds exactly, besides id, type, children and runtime `$` fields. */
  fields: readonly string[]
  write: (block: ExtractBlock<T>, content: string) => string
  /** Whether the element reads back as this block; the codec's own Markdown decides where it marks the block. */
  holds?: (block: ExtractBlock<T>, markdown: TableCellMarkdown) => boolean
}

// Cell content is inline (a cell is an inline-level slot). A block of any other kind, or with any other persisted field,
// is written as its own Markdown inside `<md-inline>`, so a cell holds every inline block the document codec does.
const HTML_ELEMENTS: { [T in Block['type']]?: HtmlElementWriter<T> } = {
  text: { fields: ['text'], write: (block) => escapeText(block.text) },
  chars: { fields: ['text', 'format'], write: (block) => {
    let text = escapeText(block.text.map((part) => part.text).join(''))
    for (const [flag, tag] of [[16, 'code'], [8, 'u'], [4, 's'], [2, 'em'], [1, 'strong']] as const) {
      if ((block.format ?? 0) & flag) text = `<${tag}>${text}</${tag}>`
    }
    return text
  } },
  br: { fields: [], write: () => '<br>' },
  // A link whose text reads as a page card is marked in its Markdown (`{type="link"}`), which `<a>` cannot carry.
  a: { fields: ['href'], write: (block, content) => `<a href="${escape(block.href)}">${content}</a>`,
    holds: (block, markdown) => !markdown([block]).endsWith('{type="link"}') },
  em: { fields: ['color'], write: (block, content) => `<mark style="background-color:${escape(block.color)}">${content}</mark>` },
  l: { fields: ['lang'], write: (block, content) => `<span lang="${escape(block.lang)}">${content}</span>` },
}

// The HTML reader's text escapes predate mentions and inline directives, so it reads `@name` and `::name` in text as
// syntax (stored tables mean that), and it drops spaces beside a line break; such text keeps its Markdown.
const READER_SYNTAX = /(?:^|\s)@[\w-]|::| \n|\n /u

function htmlElement(block: Block, markdown: TableCellMarkdown): HtmlElementWriter<Block['type']> | undefined {
  const element = HTML_ELEMENTS[block.type] as HtmlElementWriter<Block['type']> | undefined
  if (!element) return undefined
  const text = block.type === 'text' ? block.text : block.type === 'chars' ? block.text.map((part) => part.text).join('') : ''
  if (READER_SYNTAX.test(text) || (element.holds && !element.holds(block, markdown))) return undefined
  return Object.keys(block).every((key) => key === 'id' || key === 'type' || key === 'children' ||
    key.startsWith('$') || element.fields.includes(key)) ? element : undefined
}

export function blocksToTableHtml(children: Blocks, markdown: TableCellMarkdown): string {
  const html = (blocks: Blocks): string => blocksToTableHtml(blocks, markdown)
  // Equal adjacent runs need one HTML element: </code><code> would read as literal backticks,
  // and </strong><strong> as an emphasis delimiter run inside the text.
  const runs: Blocks = []
  for (const block of children) {
    const previous = runs.at(-1)
    if (block.type === 'chars' && previous?.type === 'chars' &&
      (block.format ?? 0) === (previous.format ?? 0) &&
      htmlElement(block, markdown) && htmlElement(previous, markdown)) {
      runs[runs.length - 1] = { ...previous, text: [...previous.text, ...block.text] }
    } else runs.push(block)
  }
  return runs.map((block): string => {
    const element = htmlElement(block, markdown)
    if (element) return element.write(block, 'children' in block ? html(block.children as Blocks) : '')
    return `<md-inline>${escapeText(markdown([block]))}</md-inline>`
  }).join('')
}

// A table is one line of Markdown: a line break inside it would take a container's prefix (`> `, list indent) as text.
function escapeText(text: string): string {
  return escape(text).replaceAll('\n', '&#10;')
}

function htmlText(node: HtmlElement | string): string {
  return typeof node === 'string' ? node : node.children.map(htmlText).join('')
}

function htmlMarkup(node: HtmlElement | string): string {
  if (typeof node === 'string') return escape(node)
  const attrs = Object.entries(node.attrs).map(([key, value]) => ` ${key}="${escape(value)}"`).join('')
  const open = `<${node.tag}${attrs}>`
  return ['br', 'hr', 'img', 'col'].includes(node.tag) ? open : open + node.children.map(htmlMarkup).join('') + `</${node.tag}>`
}
